import axios, { AxiosInstance } from 'axios';

interface DownloadFileParams {
  fileId: number;
  originalFilename: string;
  baseUrl: string;
  axiosInstance?: AxiosInstance;
  documentRef?: Document;
  windowRef?: Window & typeof globalThis;
}

interface ValidateFilenameResult {
  valid: boolean;
  filename: string;
  error?: string;
}

const DEFAULT_EXTENSION = '.csv';

const getDefaultDocument = () => (typeof document !== 'undefined' ? document : undefined);
const getDefaultWindow = () => (typeof window !== 'undefined' ? window : undefined);

export const appendCsvExtension = (filename: string) => {
  const trimmed = filename.trim();
  if (!trimmed) return '';
  return trimmed.toLowerCase().endsWith(DEFAULT_EXTENSION)
    ? trimmed
    : `${trimmed}${DEFAULT_EXTENSION}`;
};

export const validateFilename = (input: string): ValidateFilenameResult => {
  const trimmed = input.trim();
  if (!trimmed) {
    return {
      valid: false,
      filename: '',
      error: 'ファイル名を入力してください。',
    };
  }

  return {
    valid: true,
    filename: appendCsvExtension(trimmed),
  };
};

const extractFilename = (contentDisposition: string | undefined, fallback: string) => {
  if (!contentDisposition) {
    return fallback;
  }
  const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  if (filenameMatch && filenameMatch[1]) {
    try {
      return decodeURIComponent(escape(filenameMatch[1]));
    } catch (error) {
      console.warn('Failed to decode filename from header:', error);
      return filenameMatch[1];
    }
  }
  return fallback;
};

export const downloadFile = async ({
  fileId,
  originalFilename,
  baseUrl,
  axiosInstance = axios,
  documentRef = getDefaultDocument(),
  windowRef = getDefaultWindow(),
}: DownloadFileParams) => {
  if (!documentRef || !windowRef) {
    throw new Error('ブラウザ環境外ではファイルをダウンロードできません。');
  }

  const response = await axiosInstance.get(`${baseUrl}/api/files/${fileId}/download`, {
    responseType: 'blob',
  });

  const filename = extractFilename(response.headers['content-disposition'], originalFilename);

  const url = windowRef.URL.createObjectURL(new Blob([response.data]));
  const link = documentRef.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  documentRef.body.appendChild(link);
  link.click();
  documentRef.body.removeChild(link);
  windowRef.URL.revokeObjectURL(url);

  return filename;
};

export type { ValidateFilenameResult };
